import React from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { TableSort } from './DataTableSorting';

type DataTableHeaderCellProps = {
    id: string;
    label: string;
    sortField?: string;
    sort: TableSort | null;
    width?: string | number;
    onSort?: (field: string) => void;
    onResizeStart?: (event: React.MouseEvent, columnId: string) => void;
    onResizeReset?: (columnId: string) => void;
};

const DataTableHeaderCell = ({
    id,
    label,
    sortField,
    sort,
    width,
    onSort,
    onResizeStart,
    onResizeReset,
}: DataTableHeaderCellProps) => {
    const isSortable = !!sortField && !!onSort;
    const isActive = isSortable && sort?.field === sortField;
    const ariaSort = isActive
        ? (sort?.direction === 'asc' ? 'ascending' : 'descending')
        : undefined;

    return (
        <th
            className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
            style={{
                color: 'var(--text-tertiary)',
                width,
                position: 'relative',
                overflow: 'hidden',
            }}
            aria-sort={ariaSort}
            scope="col"
        >
            {isSortable ? (
                <button
                    type="button"
                    onClick={() => onSort(sortField)}
                    aria-label={`Sort by ${label}`}
                    className="group flex w-full items-center gap-1 text-left uppercase tracking-wider"
                    style={{ color: 'inherit', background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}
                    title={`Sort by ${label}`}
                >
                    <span>{label}</span>
                    {isActive ? (
                        sort?.direction === 'asc'
                            ? <ChevronUp size={14} aria-hidden="true" style={{ color: 'var(--accent)' }} />
                            : <ChevronDown size={14} aria-hidden="true" style={{ color: 'var(--accent)' }} />
                    ) : (
                        <ChevronsUpDown
                            size={14}
                            aria-hidden="true"
                            style={{ color: 'var(--text-tertiary)' }}
                            className="opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                        />
                    )}
                </button>
            ) : (
                <span>{label}</span>
            )}

            {onResizeStart && (
                <div
                    role="separator"
                    aria-orientation="vertical"
                    aria-label={`Resize ${label} column`}
                    title="Drag to resize; double-click to reset"
                    onMouseDown={(event) => onResizeStart(event, id)}
                    onDoubleClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onResizeReset?.(id);
                    }}
                    style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: 6,
                        cursor: 'col-resize',
                        zIndex: 1,
                        borderRight: '2px solid transparent',
                        transition: 'border-color 0.15s',
                    }}
                    onMouseEnter={(event) => { event.currentTarget.style.borderRightColor = 'var(--accent)'; }}
                    onMouseLeave={(event) => { event.currentTarget.style.borderRightColor = 'transparent'; }}
                />
            )}
        </th>
    );
};

export default DataTableHeaderCell;
